import React, { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text } from 'react-konva';
import tireImageSrc from '../../../images/pneumatico.png';
import texture2Image from '../../../images/graniglia-pav.png';
import texture4Image from '../../../images/subgrade.png';

interface Props {
    layerHeights?: number[]; // Array con altezze dei 2 strati (reso opzionale con default)
    differenziali?: number[]; // Array con i valori differenziali (reso opzionale con default)
    projectType?: 'container_yard' | 'parking_deck' | string;
}

// Valori di default per le props
const defaultLayerHeights = [300, 120];
const defaultDifferenziali = [0, 0];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TireAndLayers: React.FC<Props> = ({ layerHeights = defaultLayerHeights, differenziali = defaultDifferenziali, projectType = 'container_yard' }) => {
    const layersTotalHeight = layerHeights.reduce((acc, h) => acc + h, 0);
    const squareWidth = 400; // Larghezza dei rettangoli
    // --- Image constants and dynamic selection ---
    const IMAGE_TOP_PADDING = 2;
    const TIRE_RAPPORTO_DIAGONALE = 2.2; // 350 / 150
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;

    // In Pavements module, always use tire image regardless of projectType
    const imageSrcToLoad = tireImageSrc; // Always use tire image in Pavements
    const currentImageHeight = TIRE_IMAGE_HEIGHT; // Always use tire dimensions in Pavements
    const currentImageWidth = TIRE_IMAGE_WIDTH; // Always use tire dimensions in Pavements
    // --- NUOVE COSTANTI PER IL LAYOUT ---
    const titleAreaWidth = 160; // Spazio dedicato ai titoli a sinistra
    const rectStartX = titleAreaWidth; // Inizio dei rettangoli
    const arrowLength = 60; // Lunghezza della freccia
    const arrowGap = 10; // Spazio tra rettangolo e inizio freccia
    const labelGap = 10; // Spazio tra fine freccia ed etichetta 'd'
    const stageWidth = rectStartX + squareWidth + arrowGap + arrowLength + labelGap + 60; // Larghezza totale calcolata + margine
    const stageHeight = IMAGE_TOP_PADDING + currentImageHeight + layersTotalHeight + 10; /* bottom padding */

    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    const titles = ['Base Course', 'Subgrade'];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const strati = ['d1', 'd2']; // Etichette da aggiungere

    useEffect(() => {
        const img = new window.Image();
        img.src = imageSrcToLoad; // Use dynamic image source
        img.onload = () => setImage(img);
        img.onerror = () => {};

        const textures = [texture2Image, texture4Image];
        const setters = [setTexture2Img, setTexture4Img];

        textures.forEach((src, index) => {
            const tex = new window.Image();
            tex.src = src;
            tex.onload = () => setters[index](tex);
            tex.onerror = e => {};
        });
    }, [imageSrcToLoad]);

    // Forza la lunghezza a 2 elementi (Base Course, Subgrade)
    const safeLayerHeights = [layerHeights[0] ?? 0, layerHeights[1] ?? 0];
    const safeTitles = titles.slice(0, 2);
    const safeTextures = [texture2Img, texture4Img];

    return (
        <Stage width={stageWidth} height={stageHeight} x={-50}>
            <Layer>
                {/* Disegna i rettangoli con il nuovo layout */}
                {safeLayerHeights.map((height, index) => {
                    if (!height || height <= 0) return null;
                    // Calcolo Y (parte da sotto l'immagine)
                    const rectY = IMAGE_TOP_PADDING + currentImageHeight + safeLayerHeights.slice(0, index).reduce((acc, h) => acc + h, 0);
                    const centerY = rectY + height / 2;

                    // Texture e titolo coerenti
                    const currentTexture = safeTextures[index];
                    const currentTitle = safeTitles[index];

                    // Gestione diversa della texture per Subgrade (secondo livello)
                    let textureScale = undefined;
                    let fillPatternRepeat: 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat' = 'repeat-y';
                    if (index === 1 && currentTexture) {
                        // SUBGRADE: no-repeat, scala la texture per riempire il rettangolo
                        fillPatternRepeat = 'repeat';
                        // Calcola la scala per adattare la texture alla dimensione del rettangolo
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const texWidth = currentTexture.width || 1;
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const texHeight = currentTexture.height || 1;
                        textureScale = { x: 0.5, y: 0.5 };
                    } else if (index === 0) {
                        // BASE COURSE: repeat-y, nessuna scala
                        fillPatternRepeat = 'repeat-y';
                        textureScale = undefined;
                    }

                    return (
                        <React.Fragment key={index}>
                            {/* Titolo a Sinistra solo se height > 0 */}
                            <Text text={currentTitle} x={5} y={centerY - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />
                            {/* Rettangolo Strato */}
                            <Rect
                                x={rectStartX}
                                y={rectY}
                                width={squareWidth}
                                height={height}
                                fillPatternImage={currentTexture || undefined}
                                fillPatternRepeat={fillPatternRepeat}
                                fillPatternScale={textureScale}
                                stroke="grey"
                                strokeWidth={0.5}
                            />
                            {/* Bordo superiore solo per Base Course */}
                            {index === 0 && <Rect x={rectStartX} y={rectY} width={squareWidth} height={3} fill="#e4cab7" listening={false} />}
                        </React.Fragment>
                    );
                })}

                {/* Immagine Pneumatico */}
                {image && (
                    <KonvaImage
                        image={image}
                        x={rectStartX + squareWidth / 2 - currentImageWidth / 2} // Centro esatto sopra il rettangolo
                        y={IMAGE_TOP_PADDING}
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}
            </Layer>
        </Stage>
    );
};

export default TireAndLayers;
